import { useState } from 'react';
import { showErrorToast, showSuccessToast } from 'features/toastSlice';
import Button from 'components/Button';
import App from 'pages/organization/members/template';
import { useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { updateMember, updatePersonalInfoMember } from 'api/portal';
import { useTranslation } from 'react-i18next';
import { useFetchMember } from 'hooks/useFetchMember';
import Fade from 'components/Fade';
import { Box } from '@mui/material';
import MemberPersonalInfoSection from './MemberPersonalInfoSection';
import MemberPermissionsSection from './MemberPermissionsSection';
import {
  buildPersonalInfoFormData,
  mapMemberToPersonalInfoValues,
  validatePersonalInfo,
} from './memberPersonalInfoUtils';
import {
  buildPermissionsPayload,
  mapMemberToPermissionsValues,
  validatePermissions,
} from './memberPermissionsUtils';

const EMPTY_PERSONAL_INFO_VALUES = {
  photo: '',
  photoFile: undefined,
  username: '',
  firstName: '',
  lastName: '',
  birthYear: '',
};

const EMPTY_PERMISSIONS_VALUES = {
  email: '',
  phoneNumber: '',
  selectedPermissions: [],
  selectedUnits: [],
};

function MemberPersonalInfo() {
  const [member, setMember] = useState({});
  const [personalValues, setPersonalValues] = useState(EMPTY_PERSONAL_INFO_VALUES);
  const [permissionsValues, setPermissionsValues] = useState(EMPTY_PERMISSIONS_VALUES);
  const [personalErrors, setPersonalErrors] = useState(new Map());
  const [permissionsErrors, setPermissionsErrors] = useState(new Map());
  const [isSaving, setIsSaving] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = useParams();
  const { memberId } = params;

  const { t } = useTranslation();

  const personalValidationMessages = {
    FILL_REQUIRED: t('messages.fill-required'),
  };

  const permissionsValidationMessages = {
    INVALID_EMAIL_ERROR: t('messages.invalid-email'),
    FILL_REQUIRED: t('logs.activities-report.messages.fill-required'),
  };

  useFetchMember({
    memberId,
    onSuccess: (data) => {
      setMember(data);
      setPersonalValues(mapMemberToPersonalInfoValues(data));
      setPermissionsValues(mapMemberToPermissionsValues(data));
    },
    onFailure: () => {
      dispatch(showErrorToast('messages.load-failure'));
      navigate('/organization/members');
    },
  });

  const handlePersonalChange = (updates) => {
    setPersonalValues((currentValues) => ({ ...currentValues, ...updates }));
  };

  const handlePermissionsChange = (updates) => {
    setPermissionsValues((currentValues) => ({ ...currentValues, ...updates }));
  };

  const onSave = async () => {
    const personalValidationErrors = validatePersonalInfo(personalValues, personalValidationMessages);
    const permissionsValidationErrors = validatePermissions(permissionsValues, permissionsValidationMessages);

    setPersonalErrors(personalValidationErrors);
    setPermissionsErrors(permissionsValidationErrors);

    if (personalValidationErrors.size || permissionsValidationErrors.size) {
      dispatch(showErrorToast('messages.fix-fields'));
      return;
    }

    setIsSaving(true);

    try {
      await updatePersonalInfoMember(memberId, buildPersonalInfoFormData(personalValues));
    } catch {
      dispatch(showErrorToast('account.member-edit-info.save-failure-message'));
      setIsSaving(false);
      return;
    }

    try {
      await updateMember(memberId, buildPermissionsPayload(permissionsValues));
      dispatch(showSuccessToast('messages.save-success'));
    } catch {
      dispatch(showErrorToast('messages.save-partial-failure'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <App>
      <Fade>
        <MemberPersonalInfoSection
          member={member}
          values={personalValues}
          errors={personalErrors}
          onChange={handlePersonalChange}
        />
        <MemberPermissionsSection
          member={member}
          values={permissionsValues}
          errors={permissionsErrors}
          onChange={handlePermissionsChange}
        />
        <Box>
          <Button
            variant="outlined"
            loading={isSaving}
            onClick={onSave}
            disabled={!member.id}
            type="submit"
            data-testid="save-button"
          >
            {t('account.member-edit-personal-info.button-save')}
          </Button>
          <Button
            sx={{ margin: '0 0 0 16px' }}
            variant="outlined"
            onClick={() => navigate('/organization/members')}
            data-testid="cancel-button"
          >
            {t('organization.organization-members-edit.button-cancel')}
          </Button>
        </Box>
      </Fade>
    </App>
  );
}

export default MemberPersonalInfo;
